/**
 * AuthCallbackScreen - Handles OAuth callback redirects from Google/providers
 * 
 * Google OAuth redirects back to superjarvis.ai/auth/callback with code + state params.
 * This screen captures them and relays to orbit-gateway for token exchange.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { AtomIcon } from 'lucide-react';

const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';

export default function AuthCallbackScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Completing connection...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setMessage(`Connection failed: ${error}`);
      setTimeout(() => navigate('/integrations/calendar', { replace: true }), 3000);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setMessage('Missing authorization parameters');
      setTimeout(() => navigate('/', { replace: true }), 2000);
      return;
    }

    // Relay code + state to orbit-gateway for token exchange.
    // Must wait for Firebase auth to restore from persistence first —
    // on a fresh page load after redirect, auth.currentUser is null until
    // onAuthStateChanged fires.
    (async () => {
      try {
        const auth = getAuth();

        // Wait for Firebase auth to be ready (up to 10s)
        const user = await new Promise<import('firebase/auth').User | null>((resolve) => {
          if (auth.currentUser) {
            resolve(auth.currentUser);
            return;
          }
          const timeout = setTimeout(() => resolve(null), 10000);
          const unsubscribe = auth.onAuthStateChanged((u) => {
            clearTimeout(timeout);
            unsubscribe();
            resolve(u);
          });
        });

        const idToken = user ? await user.getIdToken() : null;

        if (!idToken) {
          console.error('[AuthCallback] No Firebase auth after waiting — cannot relay OAuth code');
          setStatus('error');
          setMessage('Authentication required. Please sign in and try connecting again.');
          setTimeout(() => navigate('/', { replace: true }), 3000);
          return;
        }

        const response = await fetch(`${ORBIT_GATEWAY_URL}/integrations/oauth-callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({ code, state }),
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage(`${data.provider || 'Service'} connected successfully!`);
          const provider = data.provider || '';
          const pendingTrustDripPrompt = sessionStorage.getItem('pendingTrustDripPrompt');
          const hasPendingTrustDrip = !!pendingTrustDripPrompt;
          if (hasPendingTrustDrip) {
            sessionStorage.removeItem('pendingTrustDripPrompt');
            sessionStorage.removeItem('pendingTrustDripProvider');
          }
          const redirectTo = hasPendingTrustDrip
            ? `/chat?message=${encodeURIComponent(pendingTrustDripPrompt || '')}`
            : provider.includes('calendar')
              ? '/integrations/calendar'
              : provider.includes('gmail')
                ? '/integrations/email'
                : '/';
          setTimeout(() => navigate(redirectTo, { replace: true }), 1500);
        } else {
          setStatus('error');
          setMessage(data.error || 'Connection failed');
          setTimeout(() => navigate('/integrations/calendar', { replace: true }), 3000);
        }
      } catch (err) {
        console.error('[AuthCallback] Error:', err);
        setStatus('error');
        setMessage('Failed to complete connection');
        setTimeout(() => navigate('/integrations/calendar', { replace: true }), 3000);
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center space-y-4">
        {status === 'processing' && (
          <AtomIcon className="w-12 h-12 text-cyan-400 animate-spin mx-auto" />
        )}
        {status === 'success' && (
          <div className="w-12 h-12 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center mx-auto">
            <span className="text-green-400 text-2xl">✓</span>
          </div>
        )}
        {status === 'error' && (
          <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-400/40 flex items-center justify-center mx-auto">
            <span className="text-red-400 text-2xl">✗</span>
          </div>
        )}
        <p className={`text-lg ${
          status === 'success' ? 'text-green-400' : 
          status === 'error' ? 'text-red-400' : 'text-cyan-400'
        }`}>
          {message}
        </p>
      </div>
    </div>
  );
}
